import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Email,
  Search,
  Visibility,
  LocalHospital,
  Phone,
  Close,
  PictureAsPdf,
  LocationOn
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useAuth } from '../../contexts/AuthContext';
import { getCacheBustedImageUrl } from '../../utils/s3Utils';

const API_URL = process.env.REACT_APP_API_URL;

interface DoctorOption {
  id: string;
  name: string;
  specialization: string;
  doctor_code?: string;
}

interface Patient {
  id: string;
  patient_id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  email: string;
  medical_history: string;
  allergies: string;
  doctor_id: string;
  doctor_name: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_visit_date: string | null;
  last_sync_date: string | null;
  profile_image?: string;
  subscription_plan_end_date?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
}

interface PlaceSuggestion {
  description: string;
  place_id: string;
  lat?: number;
  lon?: number;
}

export default function PatientsPage() {
  const { user, isLoading } = useAuth();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    age: '',  // Keep as string for form input
    gender: '',
    phone: '',
    email: '',
    medicalHistory: '',
    allergies: '',
    doctorId: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    location: '',
    latitude: null as number | null,
    longitude: null as number | null
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    age: '',
    gender: '',
    phone: '',
    email: '',
    doctorId: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewPatient, setViewPatient] = useState<Patient | null>(null);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const location = useLocation();
  const dataGridRef = useRef<any>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);

  // Location autocomplete states
  const [locationInput, setLocationInput] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const isFetchingRef = useRef(false);

  // Doctor info dialog states
  const [openDoctorDialog, setOpenDoctorDialog] = useState(false);
  const [selectedPatientForDoctor, setSelectedPatientForDoctor] = useState<Patient | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [assigningDoctor, setAssigningDoctor] = useState(false);

  const userRole = user?.role?.toLowerCase() || '';

  const fetchDoctors = async () => {
    try {
      setLoadingDoctors(true);
      const token = localStorage.getItem('token');
      const role = user?.role?.toLowerCase() || '';

      let endpoint = `${API_URL}/auth/doctors`;
      let headers: any = {
        'accept': 'application/json'
      };

      // For marketing users, use the market-user/active-doctors endpoint
      if (role === 'marketinguser') {
        endpoint = `${API_URL}/marketing/market-user/active-doctors`;
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers
      });

      if (response.ok) {
        const data = await response.json();

        // Handle response structure from market-user endpoint vs auth endpoint
        const doctorsList = role === 'marketinguser' ? data.active_doctors : data;

        const doctorOptions: DoctorOption[] = doctorsList
          .filter((doctor: any) => doctor.is_active)
          .map((doctor: any) => ({
            id: doctor.id,
            name: doctor.full_name || 'N/A',
            specialization: doctor.specialization || '-',
            doctor_code: doctor.doctor_code || ''
          }));
        setDoctors(doctorOptions);
      }
    } catch (err) {
      console.error('Error fetching doctors:', err);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const fetchPatients = async (page = paginationModel.page, pageSize = paginationModel.pageSize, search = debouncedSearchTerm, filter = activeFilter) => {
    if (isFetchingRef.current) return;
    
    try {
      isFetchingRef.current = true;
      setLoading(true);
      const token = localStorage.getItem('token');
      const role = user?.role?.toLowerCase() || '';

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const realSkip = page * pageSize;
      const realLimit = pageSize;

      const params = new URLSearchParams({
        limit: realLimit.toString(),
        skip: realSkip.toString(),
        assigned_to_me: 'false'
      });

      // Add search and filter if present
      if (search) {
        params.append('search', search);
      }
      
      if (filter === 'active') {
        params.append('is_active', 'true');
      } else if (filter === 'inactive') {
        params.append('is_active', 'false');
      } else if (filter === 'renewal_due') {
        params.append('renewal_due', 'true');
      } else if (filter === 'high_risk') {
        params.append('high_risk', 'true');
      }

      let endpoint = `${API_URL}/patients/?${params.toString()}`;
      let headers: any = {
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // For marketing users, use the market-user/patients endpoint
      if (role === 'marketinguser') {
        endpoint = `${API_URL}/marketing/market-user/patients?${params.toString()}`;
      }

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch patients');
      }

      const data = await response.json();

      if (!data) {
        throw new Error('No data received from server');
      }

      let patientsList: Patient[] = [];
      let total = 0;

      // Handle the new response structure { total_count: 450, patients: [...] }
      if (data.patients && Array.isArray(data.patients)) {
        patientsList = data.patients;
        total = data.total_count || data.total || patientsList.length;
      } else if (Array.isArray(data)) {
        patientsList = data;
        total = patientsList.length;
      } else if (data && typeof data === 'object') {
        patientsList = data.data || [];
        total = data.total_count || data.total || patientsList.length;
      }

      setRowCount(total);
      setPatients(patientsList);
      setFilteredPatients(patientsList);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch patients');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Don't load patients until user is loaded
    if (isLoading || !user) return;

    // Fetch doctors list only once
    fetchDoctors();
  }, [user, isLoading]);

  // Fetch patients when pagination or filters change
  useEffect(() => {
    if (isLoading || !user) return;
    fetchPatients();
  }, [user, isLoading, paginationModel.page, paginationModel.pageSize, debouncedSearchTerm, activeFilter]);

  // Reset to first page when search or filters change
  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [debouncedSearchTerm, activeFilter]);

  // Search debounce logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle highlight from navigation state
  useEffect(() => {
    console.log('🔍 PatientsPage - useEffect triggered');
    console.log('  - location.state:', location.state);
    console.log('  - patients.length:', patients.length);

    const state = location.state as { highlightId?: string; search?: string };
    
    // If a search term is provided in state, apply it to the searchTerm
    if (state?.search && !searchTerm) {
      console.log('🔍 Applying search term from state:', state.search);
      setSearchTerm(state.search);
      setDebouncedSearchTerm(state.search);
    }

    const highlightId = state?.highlightId;
    console.log('  - highlightId from state:', highlightId);

    if (state?.highlightId && patients.length > 0) {
      const targetId = state.highlightId;
      console.log('🎯 Highlighting patient with ID:', targetId);
      console.log('  - All patient IDs:', patients.map(p => p.id));

      // Find the index of the target patient
      const targetIndex = patients.findIndex(p => p.id === targetId);
      console.log('  - Target index in patients array:', targetIndex);

      if (targetIndex === -1) {
        console.error('  - ❌ Patient not found in array!');
        return;
      }

      // Set highlighted row
      setHighlightedRowId(targetId);
      console.log('  - highlightedRowId set to:', targetId);

      // Calculate which page the row is on
      const pageSize = paginationModel.pageSize;
      const targetPage = Math.floor(targetIndex / pageSize);
      console.log('  - Target is on page:', targetPage, '(index:', targetIndex, ', pageSize:', pageSize, ')');

      // Change to the correct page
      if (paginationModel.page !== targetPage) {
        console.log('  - Changing from page', paginationModel.page, 'to page', targetPage);
        setPaginationModel({ ...paginationModel, page: targetPage });
      }

      // Wait for DataGrid to render with the correct page, then scroll
      setTimeout(() => {
        console.log('  - Attempting to scroll to element with data-id:', targetId);
        const element = document.querySelector(`[data-id="${targetId}"]`);
        console.log('  - Element found:', element);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('  - Scroll initiated');
        } else {
          console.error('  - ❌ Element not found! Trying to find all data-id elements...');
          const allDataIds = document.querySelectorAll('[data-id]');
          console.log('  - All data-id elements:', Array.from(allDataIds).map(el => el.getAttribute('data-id')));
          console.log('  - NOTE: The row might be on a different page. Target index:', targetIndex, 'Target page:', targetPage);
        }
      }, 300); // Reduced delay to 500ms for more responsive scrolling

      // Clear highlight after animation completes
      setTimeout(() => {
        console.log('  - Clearing highlight');
        setHighlightedRowId(null);
        // Clear navigation state
        window.history.replaceState({}, document.title);
      }, 10000); // Increased to 10000ms for longer blink visibility
    } else {
      if (!state?.highlightId) {
        console.log('  - ⚠️ No highlightId in state');
      }
      if (patients.length === 0) {
        console.log('  - ⚠️ Patients array is empty');
      }
    }
  }, [location.state, patients]);

  // Search debounce logic is handled by searchTerm -> debouncedSearchTerm
  // which then triggers fetchPatients via its effect dependency.

  const handleAddPatient = () => {
    if (userRole !== 'doctor' && userRole !== 'marketinguser') return null;
    setSelectedPatient(null);
    setFormData({
      name: '',
      age: '',
      gender: '',
      phone: '',
      email: '',
      medicalHistory: '',
      allergies: '',
      doctorId: user?.role === 'doctor' ? user.id : '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelationship: '',
      location: '',
      latitude: null,
      longitude: null
    });
    setFormErrors({
      name: '',
      age: '',
      gender: '',
      phone: '',
      email: '',
      doctorId: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelationship: ''
    });
    setLocationInput('');
    setPlaceSuggestions([]);
    setOpenDialog(true);
  };

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setFormData({
      name: patient.name,
      age: patient.age.toString(),
      gender: patient.gender,
      phone: patient.phone,
      email: patient.email,
      medicalHistory: patient.medical_history || '',
      allergies: patient.allergies || '',
      doctorId: patient.doctor_id || '',
      emergencyContactName: patient.emergency_contact_name || '',
      emergencyContactPhone: patient.emergency_contact_phone || '',
      emergencyContactRelationship: patient.emergency_contact_relationship || '',
      location: patient.location || '',
      latitude: patient.latitude || null,
      longitude: patient.longitude || null
    });
    setFormErrors({
      name: '',
      age: '',
      gender: '',
      phone: '',
      email: '',
      doctorId: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelationship: ''
    });
    setLocationInput(patient.location || '');
    setPlaceSuggestions([]);
    setOpenDialog(true);
  };

  const handleViewPatient = (patient: Patient) => {
    setViewPatient(patient);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewPatient(null);
  };

  const handleStatusClick = (patient: Patient) => {
    // Only open dialog for inactive patients
    if (!patient.is_active) {
      setSelectedPatientForDoctor(patient);
      setSelectedDoctorId(patient.doctor_id || '');
      setOpenDoctorDialog(true);
    }
  };

  const handleCloseDoctorDialog = () => {
    setOpenDoctorDialog(false);
    setSelectedPatientForDoctor(null);
    setSelectedDoctorId('');
    setError('');
    setSuccess('');
  };

  const handleAssignDoctor = async () => {
    if (!selectedPatientForDoctor || !selectedDoctorId) {
      setError('Please select a doctor');
      return;
    }

    try {
      setAssigningDoctor(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      // Get current user info from localStorage
      const userDataString = localStorage.getItem('user');
      let currentUserId = '';
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          currentUserId = userData.id || '';
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      const response = await fetch(`${API_URL}/patient-auth/assign-doctor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          patient_id: selectedPatientForDoctor.id,
          assigned_doctor_id: selectedDoctorId,
          market_executive_id: currentUserId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || 'Doctor assigned successfully!');
        setTimeout(async () => {
          handleCloseDoctorDialog();
          await fetchPatients();
        }, 1500);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to assign doctor');
      }
    } catch (err) {
      console.error('Error assigning doctor:', err);
      setError('An error occurred while assigning the doctor');
    } finally {
      setAssigningDoctor(false);
    }
  };

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 3) return 'Full name must be at least 3 characters';
        if (!/^[a-zA-Z\s.]+$/.test(value)) return 'Full name can only contain letters, spaces, and dots';
        return '';

      case 'age':
        if (!value) return 'Age is required';
        const age = parseInt(value);
        if (isNaN(age)) return 'Must be a valid number';
        if (age <= 0 || age > 150) return 'Age must be between 1 and 150';
        return '';

      case 'gender':
        if (!value) return 'Gender is required';
        return '';

      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        const cleanPhone = value.replace(/[\s()-]/g, '');
        if (!/^[6-9]\d{9}$/.test(cleanPhone)) return 'Invalid Indian phone number. Must be 10 digits starting with 6-9';
        return '';

      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
        return '';

      case 'emergencyContactName':
        if (!value.trim()) return 'Emergency contact name is required';
        if (value.trim().length < 3) return 'Name must be at least 3 characters';
        return '';

      case 'emergencyContactPhone':
        if (!value.trim()) return 'Emergency contact phone is required';
        const cleanEmergencyPhone = value.replace(/[\s()-]/g, '');
        if (!/^[6-9]\d{9}$/.test(cleanEmergencyPhone)) return 'Invalid Indian phone number. Must be 10 digits starting with 6-9';
        return '';

      case 'emergencyContactRelationship':
        if (!value.trim()) return 'Relationship is required';
        return '';

      case 'doctorId':
        if (!value) return 'Please select a doctor';
        return '';

      default:
        return '';
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    const error = validateField(field, value);
    setFormErrors({ ...formErrors, [field]: error });
  };

  // Fetch place suggestions from OpenStreetMap Nominatim API (FREE)
  const fetchPlaceSuggestions = async (input: string) => {
    if (!input || input.length < 3) {
      setPlaceSuggestions([]);
      return;
    }

    setLoadingPlaces(true);

    try {
      // Using Nominatim API (OpenStreetMap) - completely free, no API key needed
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(input)}&limit=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'CardiReach-HealthMonitoring/1.0' // Required by Nominatim
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const suggestions: PlaceSuggestion[] = data.map((place: any) => ({
          description: place.display_name,
          place_id: place.place_id.toString(),
          lat: parseFloat(place.lat),
          lon: parseFloat(place.lon)
        }));

        setPlaceSuggestions(suggestions);
      } else {
        setPlaceSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching place suggestions:', error);
      setPlaceSuggestions([]);
    } finally {
      setLoadingPlaces(false);
    }
  };

  // Handle location input change
  const handleLocationInputChange = (event: any, value: string) => {
    setLocationInput(value);
    fetchPlaceSuggestions(value);
  };

  // Handle location selection
  const handleLocationSelect = (event: any, value: string | PlaceSuggestion | null) => {
    // If value is a string (user typed without selecting), ignore it
    if (typeof value === 'string') {
      return;
    }

    if (value) {
      setLocationInput(value.description);

      console.log('Selected location:', {
        address: value.description,
        latitude: value.lat,
        longitude: value.lon
      });

      setFormData({
        ...formData,
        location: value.description,
        latitude: value.lat || null,
        longitude: value.lon || null
      });
    } else {
      setLocationInput('');
      setFormData({
        ...formData,
        location: '',
        latitude: null,
        longitude: null
      });
    }
  };

  const validateForm = (): boolean => {
    const errors = {
      name: validateField('name', formData.name),
      age: validateField('age', formData.age),
      gender: validateField('gender', formData.gender),
      phone: validateField('phone', formData.phone),
      email: validateField('email', formData.email),
      doctorId: validateField('doctorId', formData.doctorId),
      emergencyContactName: validateField('emergencyContactName', formData.emergencyContactName),
      emergencyContactPhone: validateField('emergencyContactPhone', formData.emergencyContactPhone),
      emergencyContactRelationship: validateField('emergencyContactRelationship', formData.emergencyContactRelationship)
    };

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleSavePatient = async () => {
    setError('');
    setSuccess('');

    // Validate form
    if (!validateForm()) {
      setError('Please fix all validation errors before submitting');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const url = selectedPatient
        ? `${API_URL}/patients/${selectedPatient.id}`
        : `${API_URL}/patients/`;

      const method = selectedPatient ? 'PUT' : 'POST';

      // Format the request payload according to API expectations
      const payload = {
        name: formData.name,
        age: Number(formData.age),
        gender: formData.gender.toLowerCase(),
        phone: formData.phone.replace(/\D/g, ''), // Remove non-digit characters
        email: formData.email.trim(),
        medical_history: formData.medicalHistory || '',
        allergies: formData.allergies || '',
        doctor_id: formData.doctorId || null,
        emergency_contact_name: formData.emergencyContactName,
        emergency_contact_phone: formData.emergencyContactPhone.replace(/\D/g, ''), // Remove non-digit characters
        emergency_contact_relationship: formData.emergencyContactRelationship,
        location: formData.location || '',
        latitude: formData.latitude,
        longitude: formData.longitude
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(`Patient ${selectedPatient ? 'updated' : 'created'} successfully!`);
        setSubmitting(false);
        setTimeout(async () => {
          setOpenDialog(false);
          await fetchPatients();
        }, 1500);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || `Failed to ${selectedPatient ? 'update' : 'create'} patient`);
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Error saving patient:', err);
      setError(`An error occurred while ${selectedPatient ? 'updating' : 'creating'} the patient`);
      setSubmitting(false);
    }
  };

  const handleDeletePatient = async (patientId: string) => {
    if (!window.confirm('Are you sure you want to delete this patient?')) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/patients/${patientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Patient deleted successfully!');
        await fetchPatients();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete patient');
      }
    } catch (err) {
      setError('An error occurred while deleting the patient');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (patientId: string, patientName: string) => {
    try {
      setDownloadingReportId(patientId);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setDownloadingReportId(null);
        return;
      }

      const response = await fetch(`${API_URL}/reports/weekly-snapshot/${patientId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/pdf' // Expecting PDF
        }
      });

      if (response.ok) {
        // Get the blob from the response
        const blob = await response.blob();

        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link element
        const a = document.createElement('a');
        a.href = url;

        // precise filename
        const filename = `Weekly_Health_Report_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        a.download = filename;

        // Trigger the download
        document.body.appendChild(a);
        a.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSuccess(`Report for ${patientName} downloaded successfully!`);
      } else {
        const errorText = await response.text();
        setError(`Failed to download report: ${errorText || response.statusText}`);
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('An error occurred while downloading the report');
    } finally {
      setDownloadingReportId(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Patient Name',
      width: 200,
      renderCell: (params) => {
        try {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={getCacheBustedImageUrl(params.row.profile_image)}
                sx={{
                  bgcolor: params.row.profile_image ? 'transparent' : 'primary.main',
                  width: 32,
                  height: 32,
                  border: params.row.profile_image ? '2px solid #e0e0e0' : 'none'
                }}
              >
                {!params.row.profile_image && (params.value?.charAt(0) || 'U')}
              </Avatar>
              {params.value || 'Unknown'}
            </Box>
          );
        } catch (error) {
          return <span>Unknown</span>;
        }
      }
    },
    {
      field: 'age',
      headerName: 'Age',
      width: 80,
      renderCell: (params) => {
        return `${params.row.age} yrs`;
      }
    },
    {
      field: 'gender',
      headerName: 'Gender',
      width: 100,
      renderCell: (params) => {
        return params.row.gender.charAt(0).toUpperCase() + params.row.gender.slice(1);
      }
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150
    },
    {
      field: 'doctor_name', // Changed from doctorName to doctor_name to match API response
      headerName: 'Doctor',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospital fontSize="small" color="primary" />
          <Typography variant="body2">
            {params.row.doctor_name || 'Not Assigned'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          color={params.row.is_active ? 'success' : 'error'}
          size="small"
          variant="outlined"
          sx={{
            minWidth: 80,
            cursor: params.row.is_active ? 'default' : 'pointer',
            '&:hover': params.row.is_active ? {} : {
              backgroundColor: 'error.light',
              borderColor: 'error.dark'
            }
          }}
          onClick={() => handleStatusClick(params.row)}
        />
      )
    },
    {
      field: 'last_sync_date',
      headerName: 'Last Sync Date',
      width: 180,
      renderCell: (params) => {
        if (!params.row.last_sync_date) {
          return <Typography variant="body2" color="text.secondary">Never synced</Typography>;
        }
        // Treat server timestamp as UTC. If API omits trailing 'Z', append it.
        const raw = params.row.last_sync_date;
        const utcIso = (typeof raw === 'string' && !raw.endsWith('Z')) ? `${raw}Z` : raw;
        const syncDate = new Date(utcIso);
        const now = new Date();
        const diffMs = now.getTime() - syncDate.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        let timeAgo = '';
        let color = 'success.main';

        if (diffDays > 7) {
          timeAgo = `${diffDays} days ago`;
          color = '#ff6f00'; // Changed from error.main to orange
        } else if (diffDays > 3) {
          timeAgo = `${diffDays} days ago`;
          color = 'warning.main';
        } else if (diffDays > 0) {
          timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
          color = 'success.main';
        } else if (diffHours > 0) {
          timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
          color = 'success.main';
        } else {
          timeAgo = 'Just now';
          color = 'success.main';
        }

        return (
          <Tooltip title={syncDate.toLocaleString()}>
            <Typography variant="body2" sx={{ color }}>
              {timeAgo}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: 'subscription_plan_end_date',
      headerName: 'Subscription End Date',
      width: 180,
      renderCell: (params) => {
        if (!params.row.subscription_plan_end_date) {
          return <Typography variant="body2" color="text.secondary">No active plan</Typography>;
        }
        const endDate = new Date(params.row.subscription_plan_end_date);
        const now = new Date();
        const isExpired = endDate < now;

        return (
          <Typography variant="body2" sx={{ color: isExpired ? 'error.main' : 'success.main', fontWeight: isExpired ? 'bold' : 'normal' }}>
            {endDate.toLocaleDateString()}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {/* View button - accessible to superadmin, doctor, and marketuser */}
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewPatient(params.row)}>
              <Visibility />
            </IconButton>
          </Tooltip>

          {/* Edit button - only accessible to doctor */}
          {user?.role === 'doctor' && (
            <Tooltip title="Edit Patient">
              <IconButton size="small" color="primary" onClick={() => handleEditPatient(params.row)}>
                <Edit />
              </IconButton>
            </Tooltip>
          )}

          {/* Delete button - only accessible to doctor */}
          {user?.role === 'doctor' && (
            <Tooltip title="Delete Patient">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeletePatient(params.row.id)}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}

          {/* Download Report button - accessible to doctor (and maybe superadmin?) based on requirement "in the patients page in the doctor panel", implying doctor role */}
          {user?.role === 'doctor' && (
            <Tooltip title={downloadingReportId === params.row.id ? "Downloading..." : "Download Weekly Report"}>
              <IconButton
                size="small"
                color="info"
                onClick={() => handleDownloadReport(params.row.id, params.row.name)}
                disabled={downloadingReportId === params.row.id}
              >
                {downloadingReportId === params.row.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <PictureAsPdf />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  const AddPatientButton = (
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={handleAddPatient}
    >
      Add New Patient
    </Button>
  );

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (isLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Global success/error messages */}
      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && !openDialog && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Patient Management
        </Typography>


        {userRole === 'doctor' || userRole === 'marketinguser' ? AddPatientButton : null}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
            gap: 2,
            alignItems: 'center'
          }}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Patients</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="renewal_due">Renewal Due</MenuItem>
                <MenuItem value="high_risk">High Risk</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card>
        <CardContent>
          <style>
            {`
              @keyframes blueBlinkAnimation {
                0%, 100% { background-color: transparent; }
                25% { background-color: rgba(33, 150, 243, 0.3); }
                50% { background-color: rgba(33, 150, 243, 0.5); }
                75% { background-color: rgba(33, 150, 243, 0.3); }
              }
              .highlighted-row {
                animation: blueBlinkAnimation 4s ease-in-out;
              }
            `}
          </style>
          <DataGrid
            ref={dataGridRef}
            rows={filteredPatients || []}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="server"
            rowCount={rowCount}
            loading={loading}
            disableRowSelectionOnClick
            autoHeight
            getRowClassName={(params) =>
              params.row.id === highlightedRowId ? 'highlighted-row' : ''
            }
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Patient Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
              {success}
            </Alert>
          )}

          {/* Profile Picture Section - Only show when editing existing patient */}
          {selectedPatient && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, mt: 2 }}>
              <Avatar
                src={getCacheBustedImageUrl(selectedPatient.profile_image)}
                sx={{
                  width: 80,
                  height: 80,
                  mb: 1,
                  bgcolor: selectedPatient.profile_image ? 'transparent' : 'primary.main',
                  fontSize: '2rem',
                  border: '3px solid',
                  borderColor: 'primary.light'
                }}
              >
                {!selectedPatient.profile_image && selectedPatient.name.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {selectedPatient.name}
              </Typography>
            </Box>
          )}

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2,
            mt: 1
          }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              required
              error={!!formErrors.name}
              helperText={formErrors.name || 'Enter patient\'s full name'}
            />
            <TextField
              fullWidth
              label="Age"
              type="text" // Changed from "number" to "text"
              value={formData.age}
              onChange={(e) => handleFieldChange('age', e.target.value)}
              required
              error={!!formErrors.age}
              helperText={formErrors.age || 'Enter age (1-150)'}
              inputProps={{
                maxLength: 3,
                pattern: '[0-9]*' // Only allow numbers
              }}
            />
            <FormControl fullWidth required error={!!formErrors.gender}>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleFieldChange('gender', e.target.value)}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
              {formErrors.gender && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {formErrors.gender}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              required
              error={!!formErrors.phone}
              helperText={formErrors.phone || 'Enter 10 digit Indian phone number (e.g., 9876543210)'}
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              required
              error={!!formErrors.email}
              helperText={formErrors.email || 'Enter a valid email address'}
            />
            {userRole !== 'doctor' && (
              <FormControl fullWidth required error={!!formErrors.doctorId} sx={{ gridColumn: '1 / -1' }}>
                <InputLabel>Assign Doctor</InputLabel>
                <Select
                  value={formData.doctorId}
                  onChange={(e) => handleFieldChange('doctorId', e.target.value)}
                  label="Assign Doctor"
                  disabled={user?.role === 'doctor'} // Still keep for safety, but field will be hidden
                >
                  <MenuItem value="">
                    <em>Select a doctor</em>
                  </MenuItem>
                  {doctors.map((doctor) => (
                    <MenuItem
                      key={doctor.id}
                      value={doctor.id}
                      // Show all doctors to superadmin and marketuser
                      style={{
                        display: user?.role === 'doctor' && doctor.id !== user.id ? 'none' : 'flex'
                      }}
                    >
                      {doctor.name} {doctor.doctor_code ? `(${doctor.doctor_code})` : ''} - {doctor.specialization}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.doctorId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.doctorId}
                  </Typography>
                )}
                {loadingDoctors && (
                  <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75 }}>
                    Loading doctors...
                  </Typography>
                )}
              </FormControl>
            )}
            <TextField
              fullWidth
              label="Medical History"
              multiline
              rows={3}
              value={formData.medicalHistory}
              onChange={(e) => setFormData({ ...formData, medicalHistory: e.target.value })}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              fullWidth
              label="Allergies"
              multiline
              rows={3}
              value={formData.allergies}
              onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
              placeholder="e.g., Penicillin, Peanuts, Latex"
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              fullWidth
              label="Emergency Contact Name"
              value={formData.emergencyContactName}
              onChange={(e) => handleFieldChange('emergencyContactName', e.target.value)}
              required
              error={!!formErrors.emergencyContactName}
              helperText={formErrors.emergencyContactName || 'Enter emergency contact name'}
            />
            <TextField
              fullWidth
              label="Emergency Contact Phone"
              value={formData.emergencyContactPhone}
              onChange={(e) => handleFieldChange('emergencyContactPhone', e.target.value)}
              required
              error={!!formErrors.emergencyContactPhone}
              helperText={formErrors.emergencyContactPhone || 'Enter 10 digit Indian phone number (e.g., 9876543210)'}
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              fullWidth
              label="Relationship"
              value={formData.emergencyContactRelationship}
              onChange={(e) => handleFieldChange('emergencyContactRelationship', e.target.value)}
              required
              error={!!formErrors.emergencyContactRelationship}
              helperText={formErrors.emergencyContactRelationship || 'e.g., Spouse, Parent, Sibling'}
            />
            <Autocomplete
              freeSolo
              options={placeSuggestions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.description}
              filterOptions={(x) => x}
              loading={loadingPlaces}
              inputValue={locationInput}
              onInputChange={handleLocationInputChange}
              onChange={handleLocationSelect}
              sx={{ gridColumn: '1 / -1' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Location (Optional)"
                  helperText={
                    formData.latitude && formData.longitude
                      ? `Selected: ${formData.location} (Lat: ${formData.latitude.toFixed(6)}, Lng: ${formData.longitude.toFixed(6)})`
                      : 'Start typing to search for a location'
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loadingPlaces ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.place_id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">{option.description}</Typography>
                  </Box>
                </li>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSavePatient} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : selectedPatient ? 'Update' : 'Add'}
            {!submitting && ' Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Patient Details Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Patient Details</Typography>
          <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewPatient && (
            <Box>
              {/* Profile Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <Avatar
                  src={getCacheBustedImageUrl(viewPatient.profile_image)}
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 2,
                    bgcolor: viewPatient.profile_image ? 'transparent' : 'primary.main',
                    fontSize: '3rem',
                    border: '4px solid',
                    borderColor: 'primary.light'
                  }}
                >
                  {!viewPatient.profile_image && viewPatient.name.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {viewPatient.name}
                </Typography>
                <Chip
                  label={viewPatient.is_active ? 'ACTIVE' : 'INACTIVE'}
                  color={viewPatient.is_active ? 'success' : 'error'}
                  sx={{ mt: 1 }}
                />
              </Box>

              {/* Details Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 3,
                mb: 3
              }}>
                {/* Basic Information */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Email Address
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewPatient.email}</Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Phone Number
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewPatient.phone}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    Age & Gender
                  </Typography>
                  <Typography variant="body1">
                    {viewPatient.age} years, {viewPatient.gender.charAt(0).toUpperCase() + viewPatient.gender.slice(1)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    Assigned Doctor
                  </Typography>
                  <Typography variant="body1">
                    {viewPatient.doctor_name || 'Not Assigned'}
                  </Typography>
                </Box>

                {/* Medical Information */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    Medical History
                  </Typography>
                  <Typography variant="body1" sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                    {viewPatient.medical_history || 'No medical history recorded'}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    Allergies
                  </Typography>
                  <Typography variant="body1" sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                    {viewPatient.allergies || 'No allergies recorded'}
                  </Typography>
                </Box>

                {/* Emergency Contact Information */}
                <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Emergency Contact
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    Contact Name
                  </Typography>
                  <Typography variant="body1">
                    {viewPatient.emergency_contact_name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    Contact Phone
                  </Typography>
                  <Typography variant="body1">
                    {viewPatient.emergency_contact_phone}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    Relationship
                  </Typography>
                  <Typography variant="body1">
                    {viewPatient.emergency_contact_relationship}
                  </Typography>
                </Box>

                {/* Location Information */}
                {viewPatient.location && (
                  <>
                    <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Location
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationOn color="primary" fontSize="small" />
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                          Address
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                        {viewPatient.location}
                      </Typography>
                      {viewPatient.latitude && viewPatient.longitude && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Coordinates: {viewPatient.latitude.toFixed(6)}, {viewPatient.longitude.toFixed(6)}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>

              {/* Registration Info Card */}
              <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">Patient ID</Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {viewPatient.patient_id}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">Registered On</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {new Date(viewPatient.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">Last Visit</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {viewPatient.last_visit_date
                          ? new Date(viewPatient.last_visit_date).toLocaleDateString()
                          : 'No visits yet'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">Subscription Ends</Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{
                          color: viewPatient.subscription_plan_end_date && new Date(viewPatient.subscription_plan_end_date) < new Date()
                            ? 'error.main'
                            : 'primary.contrastText'
                        }}
                      >
                        {viewPatient.subscription_plan_end_date
                          ? new Date(viewPatient.subscription_plan_end_date).toLocaleDateString()
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog} variant="outlined">
            Close
          </Button>
          <Button
            onClick={() => {
              handleCloseViewDialog();
              if (viewPatient) handleEditPatient(viewPatient);
            }}
            variant="contained"
            startIcon={<Edit />}
          >
            Edit Patient
          </Button>
        </DialogActions>
      </Dialog>

      {/* Doctor Info Dialog - Shows when clicking Inactive status */}
      <Dialog open={openDoctorDialog} onClose={handleCloseDoctorDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Assign Doctor to Patient</Typography>
          <IconButton onClick={handleCloseDoctorDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {selectedPatientForDoctor && (
            <Box>
              {/* Patient Info */}
              <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Patient
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {selectedPatientForDoctor.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPatientForDoctor.age} years, {selectedPatientForDoctor.gender.charAt(0).toUpperCase() + selectedPatientForDoctor.gender.slice(1)}
                </Typography>
              </Box>

              {/* Doctor Selection Dropdown */}
              <FormControl fullWidth required>
                <InputLabel>Assign Doctor</InputLabel>
                <Select
                  value={selectedDoctorId}
                  onChange={(e) => setSelectedDoctorId(e.target.value)}
                  label="Assign Doctor"
                  disabled={loadingDoctors || assigningDoctor}
                >
                  <MenuItem value="">
                    <em>Select a doctor</em>
                  </MenuItem>
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <LocalHospital fontSize="small" color="primary" />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {doctor.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.specialization}
                          </Typography>
                        </Box>
                        {doctor.id === selectedPatientForDoctor.doctor_id && (
                          <Chip label="Current" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {loadingDoctors && (
                  <Typography variant="caption" sx={{ mt: 1, ml: 1.75 }}>
                    Loading doctors...
                  </Typography>
                )}
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDoctorDialog} disabled={assigningDoctor}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignDoctor}
            variant="contained"
            disabled={assigningDoctor || !selectedDoctorId || selectedDoctorId === selectedPatientForDoctor?.doctor_id}
            startIcon={assigningDoctor ? <CircularProgress size={20} /> : null}
          >
            {assigningDoctor ? 'Assigning...' : 'Assign Doctor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}